import React from "react";
import { useLocation } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";
import { FiMessageCircle } from "react-icons/fi";
import { colors } from "../../../utils";
import { PATH } from "../../../utils/config";
import { CHAT_ENABLED } from "../../../constants/featureFlags";
import { toggleDock, useDockUnread, useDockStore } from "../../ChatDock/dockStore";
import { HEADER_ICON_SIZE, IconWrapper, ChatBadge } from "./styles";
import { NavEntryMenuItem } from "./NavEntryMenuItem";
import { isSectionActive } from "./headerNavActive";

/**
 * Direct-messages header nav entry (GR-2). The Facebook-style chat-indicator
 * that lives in the MAIN app header (GalleryHeader, next to Feedback / Capture /
 * Notifications) and is the ENTRY POINT to DMs: clicking it toggles the
 * bottom-right MiniDmDock (the docked chat BOX), so a conversation opens docked
 * in the corner instead of the full /messages page. The full inbox stays
 * reachable at /messages for "see all".
 *
 * Self-gates on CHAT_ENABLED (default OFF) exactly like CaptureNavEntry — while
 * the flag is off it renders null, so the header DOM is byte-identical to a build
 * without chat. Same Tooltip + IconWrapper + a11y contract as the neighbouring
 * icons; the unread count is published to the shared dockStore by MiniDmDock
 * (which fetches conversations), so this icon shows the badge without a second
 * data layer.
 *
 * NOTE: the previous GR-2 attempt added this entry to components/Navbar (used
 * only on the AiWheelGenerator / Invite pages), not GalleryHeader — the main app
 * shell (components/Wrapper) renders GalleryHeader, so the indicator never
 * appeared and the dock could never open. This is the fix.
 */
const ChatNavEntry = ({ variant = "icon", ...rest }) => {
  // Hook first (rules-of-hooks); the badge label is null until MiniDmDock
  // publishes an unread count to the store.
  const chatUnread = useDockUnread();
  const { open: dockOpen } = useDockStore();
  const { pathname } = useLocation();

  if (!CHAT_ENABLED) return null;

  const open = (el) => toggleDock(el);

  // ISP-9999: Chat is the current section either while its dock is open OR while
  // the full inbox route (/messages, /messages/:id) is showing — clicking the
  // icon opens the dock, so dock-open is the primary "you are here" signal.
  const active = dockOpen || isSectionActive(pathname, PATH.MESSAGES);

  const label = chatUnread ? `Messages, ${chatUnread} unread` : "Messages";

  if (variant === "menu") {
    return (
      <NavEntryMenuItem
        label={chatUnread ? `Messages (${chatUnread})` : "Messages"}
        ariaLabel={label}
        onActivate={(e) => open(e.currentTarget)}
        icon={
          <FiMessageCircle
            aria-hidden="true"
            style={{ color: colors.PrimaryPurple, width: 22, height: 22 }}
          />
        }
        {...rest}
      />
    );
  }

  return (
    <Tooltip title="Messages">
      <IconWrapper
        className="chat-button"
        role="button"
        tabIndex={0}
        aria-label={label}
        aria-haspopup="dialog"
        aria-current={active ? "page" : undefined}
        isActive={active}
        onClick={(e) => open(e.currentTarget)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            open(e.currentTarget);
          }
        }}
        style={{ position: "relative", cursor: "pointer" }}
      >
        <FiMessageCircle
          aria-hidden="true"
          style={{
            color: colors.PrimaryPurple,
            width: HEADER_ICON_SIZE,
            height: HEADER_ICON_SIZE,
          }}
        />
        {chatUnread ? (
          <ChatBadge aria-hidden="true">{chatUnread}</ChatBadge>
        ) : null}
      </IconWrapper>
    </Tooltip>
  );
};

export default ChatNavEntry;
